import React from "react";
import {
  Box,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { DayPicker } from "react-day-picker";
import { format, isValid, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import "react-day-picker/dist/style.css";

import { UnifiedModal } from "./UnifiedModal";
import { Button } from "./Button";
import { CustomSelector } from "./CustomSelector";
import { CustomTextField } from "./CustomTextField";
import { InfoTooltip } from "./InfoTooltip";
import { getDayPickerSx } from "./dayPickerSx.js";
import { nextRangeOnDayClick } from "../utils/dateUtils";

const ANY_OPTION_VALUE = "__any__";

const parseDateValue = (value) => {
  if (!value) return undefined;
  const parsedDate = parseISO(value);
  return isValid(parsedDate) ? parsedDate : undefined;
};

const formatDateValue = (date) => format(date, "yyyy-MM-dd");

const formatDateLabel = (value) => {
  const parsedDate = parseDateValue(value);
  return parsedDate ? format(parsedDate, "dd/MM/yyyy", { locale: es }) : "";
};

export const DateRangeFilterField = ({
  startValue,
  endValue,
  onStartChange,
  onEndChange,
}) => {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isShortViewport = useMediaQuery("(max-height: 940px)");

  const selectedRange = React.useMemo(
    () => ({
      from: parseDateValue(startValue),
      to: parseDateValue(endValue),
    }),
    [endValue, startValue]
  );

  const hasSelection = Boolean(selectedRange.from || selectedRange.to);

  const triggerLabel = React.useMemo(() => {
    if (selectedRange.from && selectedRange.to) {
      return `${formatDateLabel(startValue)} - ${formatDateLabel(endValue)}`;
    }

    if (selectedRange.from) {
      return `Desde ${formatDateLabel(startValue)}`;
    }

    if (selectedRange.to) {
      return `Hasta ${formatDateLabel(endValue)}`;
    }

    return "Selecciona un rango";
  }, [endValue, selectedRange.from, selectedRange.to, startValue]);

  const handleRangeSelect = (_selected, day) => {
    const range = nextRangeOnDayClick(selectedRange, day);
    onStartChange(formatDateValue(range.from));
    onEndChange(range.to ? formatDateValue(range.to) : "");
  };

  const handleClear = () => {
    onStartChange("");
    onEndChange("");
  };

  const triggerOptions = hasSelection
    ? [
        {
          value: "__date_range__",
          label: triggerLabel,
        },
      ]
    : [];

  return (
    <CustomSelector
      value={hasSelection ? "__date_range__" : null}
      onChange={() => {}}
      options={triggerOptions}
      placeholder="Selecciona un rango"
      searchable={false}
      menuWidth={isMobile ? 340 : 560}
      menuMaxHeight={420}
      menuEstimatedHeight={isMobile ? 320 : 360}
      renderMenuContent={({ closeDropdown, dropdownMaxHeight }) => {
        const useCompactCalendar =
          isMobile || isShortViewport || dropdownMaxHeight < 340;
        const showSingleMonth = isMobile;

        return (
        <Stack spacing={0.9}>
          <Box
            sx={getDayPickerSx(theme, {
              compact: useCompactCalendar,
              singleMonth: showSingleMonth,
            })}
          >
            <DayPicker
              mode="range"
              locale={es}
              selected={selectedRange}
              onSelect={handleRangeSelect}
              numberOfMonths={showSingleMonth ? 1 : 2}
              defaultMonth={selectedRange.from || selectedRange.to || new Date()}
              fixedWeeks
            />
          </Box>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button variant="ghost" onClick={handleClear}>
              Limpiar
            </Button>
            <Button variant="outline" onClick={closeDropdown}>
              Cerrar
            </Button>
          </Stack>
        </Stack>
        );
      }}
      menuPadding={12}
    />
  );
};

const SectionLabel = ({ icon: Icon, title, description }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
    {Icon && <Icon size={16} />}
    <Typography
      variant="subtitle2"
      sx={{ fontSize: "0.85rem", fontWeight: 600 }}
    >
      {title}
    </Typography>
    {description && (
      <InfoTooltip title={description} placement="top" zIndex={100001} />
    )}
  </Box>
);

export const IncidentFiltersModal = ({
  open,
  onClose,
  title = "Filtros avanzados",
  filterConfigs = [],
  draftFilters,
  onChange,
  onApply,
  onClearAll,
}) => {
  const decorateOptions = React.useCallback(
    (options, includeAny = true, isFeminine = false) => {
      if (!includeAny) return options;
      return [
        {
          value: ANY_OPTION_VALUE,
          label: isFeminine ? "Mostrar todas" : "Mostrar todos",
        },
        ...options,
      ];
    },
    []
  );

  const ensureOptionPresence = React.useCallback((options, fallback) => {
    if (!fallback) return options;
    const exists = options.some((option) => option.value === fallback.value);
    return exists ? options : [fallback, ...options];
  }, []);

  const handleSelectorChange = (field, optionsList) => (value) => {
    if (value === ANY_OPTION_VALUE) {
      onChange(field, null);
      return;
    }
    const nextValue = optionsList.find((option) => option.value === value) || null;
    onChange(field, nextValue);
  };

  const renderFilterSelector = (config) => {
    const {
      key,
      title,
      description,
      icon,
      options = [],
      loading = false,
      disabled = false,
      placeholder = `Selecciona ${title.toLowerCase()}`,
      loadingPlaceholder = `Cargando ${title.toLowerCase()}...`,
      fallbackOption = null,
      showForRoles = null,
      role,
      type = "selector",
      startKey,
      endKey,
      includeAny = true,
      searchable = true,
      ui = "default",
    } = config;

    // Si hay restricción de roles y no coincide, no mostrar
    if (showForRoles && !showForRoles.includes(role)) {
      return null;
    }

    if (type === "date-range") {
      if (!startKey || !endKey) return null;
      const startValue = draftFilters[startKey] || "";
      const endValue = draftFilters[endKey] || "";

      if (ui === "calendar-range") {
        return (
          <Box key={key}>
            <SectionLabel
              icon={icon}
              title={title}
              description={description}
            />
            <DateRangeFilterField
              startValue={startValue}
              endValue={endValue}
              onStartChange={(value) => onChange(startKey, value)}
              onEndChange={(value) => onChange(endKey, value)}
            />
          </Box>
        );
      }

      return (
        <Box key={key}>
          <SectionLabel
            icon={icon}
            title={title}
            description={description}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <CustomTextField
                type="date"
                value={startValue}
                onChange={(event) => onChange(startKey, event.target.value)}
                sx={{ width: "100%" }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <CustomTextField
                type="date"
                value={endValue}
                onChange={(event) => onChange(endKey, event.target.value)}
                sx={{ width: "100%" }}
              />
            </Box>
          </Stack>
        </Box>
      );
    }

    if (type === "date" || type === "month") {
      const value = draftFilters[key] || "";
      return (
        <Box key={key}>
          <SectionLabel icon={icon} title={title} description={description} />
          <CustomTextField
            type={type}
            value={value}
            onChange={(event) => onChange(key, event.target.value)}
            sx={{ width: "100%" }}
          />
        </Box>
      );
    }

    const resolvedOptions = ensureOptionPresence(options, fallbackOption);

    const currentValue = draftFilters[key]?.value ?? fallbackOption?.value ?? null;
    const isDisabled = disabled || loading || !resolvedOptions.length;
    const finalPlaceholder = loading ? loadingPlaceholder : placeholder;

    return (
      <Box key={key}>
        <SectionLabel
          icon={icon}
          title={title}
          description={description}
        />
        <CustomSelector
          value={currentValue}
          onChange={handleSelectorChange(key, resolvedOptions)}
          options={decorateOptions(resolvedOptions, includeAny, key === "agency")}
          placeholder={currentValue ? finalPlaceholder : (key === "agency" ? "Mostrar todas" : "Mostrar todos")}
          disabled={isDisabled}
          searchable={searchable}
        />
      </Box>
    );
  };

  return (
    <UnifiedModal
      open={open}
      onClose={() => onClose(false)}
      title={title}
      maxWidth="sm"
      sx={{ minHeight: '500px' }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", maxHeight: "70vh" }}>
        {/* Contenido scrollable de filtros */}
        <Box sx={{ flex: 1, overflowY: "auto", pr: 1, mb: 2 }}>
          <Stack spacing={3}>
            {filterConfigs.map((config) => renderFilterSelector(config))}
          </Stack>
        </Box>

        {/* Botones fijos en la parte inferior */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{
            pt: 3,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            justifyContent: "space-between",
            position: "sticky",
            bottom: 0,
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          <Button variant="ghost" onClick={onClearAll}>
            Limpiar filtros
          </Button>

          <Stack direction="row" spacing={1}>
            <Button variant="ghost" onClick={() => onClose(false)}>
              Cancelar
            </Button>
            <Button onClick={onApply}>Aplicar filtros</Button>
          </Stack>
        </Stack>
      </Box>
    </UnifiedModal>
  );
};
